import { useCallback, MouseEvent as MouseEventReact, Key, forwardRef } from 'react';
import { cx } from '@emotion/css';
import {
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
} from '@mui/material';

import { IToggleButtonGroup } from './toggle-button-group.models';
import { useStyles } from './toggle-button-group.styles';
import { TFormExtendedCommonProps } from '../form';
import { Tooltip } from '../tooltip';
import { ReactComponent as InfoIcon } from '../../../assets/images/icons/info/info.svg';

const TooltipTrigger = forwardRef(function TooltipTrigger(props, ref) {
  const classes = useStyles();

  return (
    // @ts-ignore
    <div {...props} ref={ref} className={classes.tooltipIcon}>
      <InfoIcon />
    </div>
  );
});

export const ToggleButtonGroup = ({
  options,
  onChange,
  onClick,
  fullWidth,
  withoutMargins,
  standalone,
  variant = 'default',
  exclusive,
  exclusiveExactly,
  error,
  helperText,
  selectedStyles,
  buttonStyles,
  formControlStyles,
  groupStyles,
  ...props
}: TFormExtendedCommonProps<IToggleButtonGroup>): JSX.Element => {
  const classes = useStyles();

  const handleChange = useCallback(
    (event: MouseEventReact<HTMLElement>, newValue: string | null) => {
      if (exclusiveExactly) {
        if (newValue) {
          onChange(event, newValue);
        }
      } else {
        onChange(newValue);
      }
    }, [exclusiveExactly, onChange],
  );

  return (
    <MuiFormControl
      error={error}
      fullWidth={fullWidth}
      classes={{
        root: cx(classes.toggleGroupRoot, formControlStyles,
          { [classes.toggleGroupRootWithoutMargins]: withoutMargins },
        ),
      }}
    >
      <MuiToggleButtonGroup
        {...props}
        exclusive={exclusive || exclusiveExactly}
        classes={{
          root: cx(groupStyles,
            { [classes.toggleGroupRootStandalone]: standalone },
          ),
          groupedVertical: cx(classes.groupedVertical, {
            [classes.groupedVerticalStandalone]: standalone,
          }),
        }}
        onChange={handleChange}
        onClick={onClick}
      >
        {options.map(
          ({ children, value, tooltip, testId }) => (
            <MuiToggleButton
              key={value as Key}
              value={value}
              classes={{
                root: cx(classes.toggleButtonRoot, buttonStyles,
                  { [classes.toggleButtonRootFullWidth]: fullWidth },
                  { [classes.toggleButtonRootStandalone]: standalone },
                  { [classes.toggleButtonRootOutlined]: variant === 'outlined' },
                  { [classes.toggleButtonRootOutlinedLight]: variant === 'outlined-light' },
                  // to fix divider color issue
                  { [classes.outlinedStandaloneDivider]: standalone && variant === 'outlined'
                    || variant === 'outlined-light' },
                ),
                selected: selectedStyles || classes.toggleButtonSelected,
                sizeSmall: classes.sizeSmall,
                sizeMedium: classes.sizeMedium,
              }}
              data-testid={testId ?? `toggle-button-${String(value)}`} // Don't remove data-testid, it is used by Yandex Metrica
            >
              {children}
              {tooltip && (
                <Tooltip
                  title={tooltip as string}
                  withArrow
                >
                  <TooltipTrigger />
                </Tooltip>
              )}
            </MuiToggleButton>
          ),
        )}
      </MuiToggleButtonGroup>
      {helperText && (
        <MuiFormHelperText
          classes={{
            root: classes.formHelperTextRoot,
            error: classes.formHelperTextError,
          }}
        >
          {helperText}
        </MuiFormHelperText>
      )}
    </MuiFormControl>
  );
};
